import { initialTheme } from 'theme';

/** Shape returned by licenseesbranding and used in Redux ui. */
export type BrandingConfig = {
    companyName: string;
    initialTheme: typeof initialTheme;
    images: { assetsPath: string };
    bBusinessAccountsEnabled: boolean;
    bPersonalAccountsEnabled: boolean;
};

type WindowEnv = {
    BASE_URL?: string;
    BUILD_NUMBER?: string;
    /**
     * Local dev only. Set to a preset key to mimic API branding without calling licenseesbranding.
     * Leave empty (or omit) to use the API — same as production.
     * Presets: ibanera | jhglobal | syndicate | mbme | switch
     */
    BRAND_OVERRIDE?: string;
    /** Optional overrides when using BRAND_OVERRIDE or custom brand folder */
    COMPANY_NAME?: string;
    BRAND_ASSETS_PATH?: string;
};

/**
 * Presets mirror common API responses. Assets live under public/assets/{name}/.
 * Adjust flags here only for local UX; production values come from licenseesbranding.
 */
const BRAND_PRESETS: Record<string, BrandingConfig> = {
    ibanera: {
        companyName: 'Ibanera',
        initialTheme,
        images: { assetsPath: '/assets/ibanera' },
        bBusinessAccountsEnabled: true,
        bPersonalAccountsEnabled: true,
    },
    jhglobal: {
        companyName: '75 Pillars',
        initialTheme,
        images: { assetsPath: '/assets/jhglobal' },
        bBusinessAccountsEnabled: false,
        bPersonalAccountsEnabled: true,
    },
    syndicate: {
        companyName: 'Syndicate Payments',
        initialTheme,
        images: { assetsPath: '/assets/syndicate' },
        bBusinessAccountsEnabled: true,
        bPersonalAccountsEnabled: true,
    },
    mbme: {
        companyName: 'MBME',
        initialTheme,
        images: { assetsPath: '/assets/mbme' },
        bBusinessAccountsEnabled: true,
        bPersonalAccountsEnabled: true,
    },
    switch: {
        companyName: 'Switch',
        initialTheme,
        images: { assetsPath: '/assets/switch' },
        bBusinessAccountsEnabled: true,
        bPersonalAccountsEnabled: true,
    },
};

const buildBranding = (companyName: string, assetsPath: string): BrandingConfig => ({
    companyName,
    initialTheme,
    images: { assetsPath },
    bBusinessAccountsEnabled: true,
    bPersonalAccountsEnabled: true,
});

/**
 * Optional local dev branding from public/env.js.
 * Returns null when unset — App then uses licenseesbranding API (and cache).
 */
export const getDevBrandingOverride = (): BrandingConfig | null => {
    const env = (window as Window & { env?: WindowEnv }).env;
    if (!env) {
        return null;
    }

    const brandKey = env.BRAND_OVERRIDE?.trim().toLowerCase();
    if (!brandKey) {
        if (env.COMPANY_NAME && env.BRAND_ASSETS_PATH) {
            return buildBranding(env.COMPANY_NAME, env.BRAND_ASSETS_PATH);
        }
        return null;
    }

    const preset = BRAND_PRESETS[brandKey];
    if (!preset) {
        console.warn(
            `[devBranding] Unknown BRAND_OVERRIDE "${brandKey}". ` +
                `Use one of: ${Object.keys(BRAND_PRESETS).join(', ')}`
        );
        return null;
    }

    return {
        ...preset,
        ...(env.COMPANY_NAME ? { companyName: env.COMPANY_NAME } : {}),
        ...(env.BRAND_ASSETS_PATH
            ? { images: { assetsPath: env.BRAND_ASSETS_PATH } }
            : {}),
    };
};

export const isDevBrandingOverrideEnabled = (): boolean => getDevBrandingOverride() !== null;

/**
 * Local dev: keep preset assets/flags, use API theme colors (same as production styling).
 */
export const mergeApiBrandingWithDevOverride = (
    api: BrandingConfig & { expiryDate?: string },
    dev: BrandingConfig
): BrandingConfig & { expiryDate: string } => ({
    ...api,
    companyName: dev.companyName,
    images: dev.images,
    bBusinessAccountsEnabled: dev.bBusinessAccountsEnabled,
    bPersonalAccountsEnabled: dev.bPersonalAccountsEnabled,
    initialTheme: api.initialTheme,
    expiryDate: api.expiryDate ?? '',
});

